import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ChartLineUp, Coins, Sparkle, ShieldCheck } from "@phosphor-icons/react";
import { recommendFunds } from "../lib/api";
import SEO from "../components/SEO";

const GOALS = ["Wealth Creation", "Retirement", "Tax Saving (ELSS)", "Child Education", "Emergency Fund", "Home Purchase"];
const RISKS = ["Conservative", "Moderate", "Aggressive"];

const riskColor = (r) => {
  if (!r) return "#A1A1AA";
  const l = r.toLowerCase();
  if (l === "low") return "#34C759";
  if (l === "high") return "#FF3B30";
  return "#FFCC00";
};

export default function MutualFundRecommender() {
  const navigate = useNavigate();
  const [goal, setGoal] = useState("Wealth Creation");
  const [risk, setRisk] = useState("Moderate");
  const [monthly, setMonthly] = useState(10000);
  const [years, setYears] = useState(10);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async () => {
    setLoading(true);
    setErr(null);
    setData(null);
    try {
      const out = await recommendFunds({
        goal,
        horizon_years: Number(years),
        risk,
        monthly: Number(monthly),
        region: "India",
      });
      setData(out);
    } catch (e) {
      setErr("Failed to generate recommendations. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian text-white" data-testid="funds-page">
      <SEO
        title="AI Mutual Fund Recommender (India)"
        path="/funds"
        description="Free AI mutual fund recommender for Indian investors — get personalised category allocation and example top funds matched to your goal (Wealth Creation, Retirement, ELSS Tax Saving, Child Education), risk profile and SIP amount."
        keywords={["mutual fund recommender", "best SIP funds India", "ELSS recommendation", "mutual fund AI", "best mutual funds 2026", "retirement mutual funds India"]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "AI Mutual Fund Recommender",
          url: "https://sent-i-meter.com/funds",
          applicationCategory: "FinanceApplication",
          offers: { "@type": "Offer", price: "0", priceCurrency: "INR" },
        }}
      />
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/70 border-b border-white/10 px-4 md:px-6 h-14 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white/70 hover:text-white" data-testid="funds-back">
            <ArrowLeft size={16} weight="bold" />
            <span className="overline">back</span>
          </button>
          <div className="w-px h-6 bg-white/10 mx-2" />
          <button onClick={() => navigate("/")} className="w-8 h-8 bg-white text-black flex items-center justify-center">
            <ChartLineUp size={16} weight="bold" />
          </button>
          <div className="hidden sm:block">
            <div className="heading text-sm font-bold tracking-tight leading-none">FUND RECOMMENDER</div>
            <div className="overline mt-0.5 leading-none">india mutual funds · ai-curated</div>
          </div>
        </div>
        <Coins size={18} weight="duotone" color="#FFCC00" />
      </header>

      <main className="px-4 md:px-6 py-6 max-w-[1300px] mx-auto">
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-6">
          {/* Form */}
          <div className="bg-surface border border-white/10 p-5 lg:col-span-1" data-testid="funds-form">
            <h2 className="heading text-xl font-bold mb-4">Your Profile</h2>

            <div className="mb-4">
              <div className="overline mb-2">goal</div>
              <div className="flex flex-wrap gap-1.5">
                {GOALS.map((g) => (
                  <button
                    key={g}
                    onClick={() => setGoal(g)}
                    className={`px-2.5 py-1 mono text-[10px] uppercase tracking-[0.14em] border transition-colors duration-150 ${
                      goal === g
                        ? "bg-white text-black border-white"
                        : "bg-transparent text-white/60 border-white/15 hover:border-white/40 hover:text-white"
                    }`}
                    data-testid={`funds-goal-${g.split(" ")[0]}`}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <div className="overline mb-2">risk tolerance</div>
              <div className="flex gap-1.5">
                {RISKS.map((r) => (
                  <button
                    key={r}
                    onClick={() => setRisk(r)}
                    className={`flex-1 px-3 py-1.5 mono text-xs uppercase tracking-[0.16em] border transition-colors duration-150 ${
                      risk === r
                        ? "bg-white text-black border-white"
                        : "bg-transparent text-white/60 border-white/15 hover:border-white/40 hover:text-white"
                    }`}
                    data-testid={`funds-risk-${r}`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <div className="flex items-center justify-between mb-1">
                <span className="overline">monthly SIP · ₹</span>
                <span className="mono text-sm text-white">₹{monthly.toLocaleString("en-IN")}</span>
              </div>
              <input
                type="number" min="500" max="1000000" step="500" value={monthly}
                onChange={(e) => setMonthly(Math.max(500, +e.target.value || 0))}
                className="w-full bg-black border border-white/10 px-3 py-2 mono text-sm focus:outline-none focus:border-white/30 mb-2"
                data-testid="funds-monthly"
              />
              <input
                type="range" min="500" max="200000" step="500" value={monthly}
                onChange={(e) => setMonthly(+e.target.value)}
                className="w-full accent-bullish"
              />
            </div>

            <div className="mb-5">
              <div className="flex items-center justify-between mb-1">
                <span className="overline">horizon · years</span>
                <span className="mono text-sm text-white">{years}</span>
              </div>
              <input
                type="range" min="1" max="40" step="1" value={years}
                onChange={(e) => setYears(+e.target.value)}
                className="w-full accent-bullish"
                data-testid="funds-years"
              />
            </div>

            <button
              onClick={submit}
              disabled={loading}
              className="w-full py-2.5 bg-white text-black hover:bg-white/90 transition-colors duration-150 mono text-xs uppercase tracking-[0.18em] disabled:opacity-50"
              data-testid="funds-submit"
            >
              {loading ? "Generating..." : "Get Recommendations"}
            </button>
          </div>

          {/* Output */}
          <div className="lg:col-span-2 flex flex-col gap-4 md:gap-6" data-testid="funds-results">
            {err && (
              <div className="bg-bearish/10 border border-bearish/40 p-4 mono text-sm">{err}</div>
            )}

            {!data && !loading && !err && (
              <div className="bg-surface border border-white/10 p-8 text-center">
                <Sparkle size={20} weight="duotone" color="#FFCC00" className="mx-auto mb-2" />
                <div className="heading text-base font-semibold mb-1">Tell us your goal</div>
                <div className="overline">we'll suggest fund categories matched to your profile</div>
              </div>
            )}

            {loading && (
              <div className="bg-surface border border-white/10 p-5 space-y-2">
                <div className="h-3 bg-white/5 w-full" />
                <div className="h-3 bg-white/5 w-11/12" />
                <div className="h-3 bg-white/5 w-9/12" />
                <div className="h-24 bg-white/5 w-full mt-4" />
              </div>
            )}

            {data && (
              <>
                <div className="bg-surface border border-white/10 p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkle size={16} weight="duotone" color="#FFCC00" />
                    <h3 className="heading text-lg font-semibold">Strategy</h3>
                    <span className="overline ml-auto">claude · sonnet 4.5</span>
                  </div>
                  <p className="text-sm leading-relaxed text-white/85" data-testid="funds-strategy">
                    {data?.summary}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="funds-cards">
                  {(data?.recommendations || []).map((r, i) => (
                    <div key={i} className="bg-surface border border-white/10 p-5" data-testid={`funds-rec-${i}`}>
                      <div className="flex items-baseline justify-between mb-1">
                        <h4 className="heading text-base font-semibold">{r.category}</h4>
                        <span className="mono text-2xl font-bold text-neutral_zone">{r.allocation_pct}%</span>
                      </div>
                      <div className="flex items-center gap-3 mb-3 border-b border-white/5 pb-3">
                        <span className="mono text-[10px] uppercase tracking-[0.14em] px-2 py-0.5" style={{ color: riskColor(r.risk_level), border: `1px solid ${riskColor(r.risk_level)}33` }}>
                          {r.risk_level} risk
                        </span>
                        <span className="mono text-[11px] text-white/70">return ≈ {r.expected_return_range}</span>
                      </div>
                      <p className="text-xs text-white/70 mb-3 leading-relaxed">{r.rationale}</p>
                      <div className="overline mb-1.5">example top funds</div>
                      <div className="space-y-1">
                        {(r.example_funds || []).map((f, j) => (
                          <div key={j} className="mono text-xs text-white/85 px-2 py-1 border border-white/5">{f}</div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-surface border border-white/10 p-4 flex items-start gap-2" data-testid="funds-disclaimer">
                  <ShieldCheck size={14} weight="bold" color="#A1A1AA" className="mt-0.5 shrink-0" />
                  <p className="text-[11px] text-white/55 leading-relaxed">{data?.disclaimer}</p>
                </div>
              </>
            )}
          </div>
        </section>

        <footer className="border-t border-white/10 pt-4 mt-8 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="overline hover:text-white" data-testid="funds-footer-back">← back to dashboard</button>
          <span className="overline">educational · not investment advice</span>
        </footer>
      </main>
    </div>
  );
}
